import './index.scss';

import { Component, MouseEvent, ReactNode } from 'react';

import { BraxIcon } from 'components/BraxIcon';

export type BraxNavbarItem<T extends string | number = string | number> = {
    key: T,
    content: ReactNode,
    disabled: boolean
};

export type BraxNavbarProps<T extends string | number = string | number> = {
    active?: T,
    itens: BraxNavbarItem<T>[],
    onChange: (active: T) => void
};

type BraxNavbarState<T extends string | number = string | number> = {
    active?: T,
    scrollable: boolean,
    scrollPosition: 'start' | 'middle' | 'end'
};

export class BraxNavbar<T extends string | number = string | number> extends Component<BraxNavbarProps<T>, BraxNavbarState<T>> {

    state: BraxNavbarState<T> = {
        active: this.props.active,
        scrollable: false,
        scrollPosition: 'start'
    };

    navRef: HTMLElement | null = null;

    componentDidMount() {
        window.addEventListener('resize', () => this.checkSize());
    }

    componentDidUpdate(prevProps: BraxNavbarProps<T>) {
        if (prevProps.active !== this.props.active)
            this.setState({ active: this.props.active });
    }

    componentWillUnmount() {
        window.removeEventListener('resize', () => this.checkSize());
    }

    setRef(ref: HTMLElement | null) {
        if (this.navRef)
            return;

        this.navRef = ref;

        this.checkSize();
    }

    checkSize() {
        this.setState(prevState => {
            if (!this.navRef)
                return null;

            const scrollable = this.navRef.scrollWidth > this.navRef.clientWidth;

            const scrollPosition = this.navRef.scrollLeft === 0
                ? 'start'
            : this.navRef.scrollLeft === this.navRef.scrollWidth - this.navRef.clientWidth
                ? 'end'
                : 'middle'

            return prevState.scrollable !== scrollable || prevState.scrollPosition !== scrollPosition
                ? { scrollable, scrollPosition }
                : null;
        });
    }

    scroll(direction: 1 | -1) {
        if (direction == -1 && this.state.scrollPosition === 'start')
            return;
        else if (direction == +1 && this.state.scrollPosition === 'end')
            return;

        this.navRef?.scroll({ left: this.navRef.scrollLeft + this.navRef.clientWidth * direction, behavior: 'smooth' });
    }

    change(active: T, event?: MouseEvent) {
        if (event)
            event.preventDefault();

        if (this.state.active !== active) {
            this.setState({ active });

            this.props.onChange?.(active);
        }
    }

    render() {
        return (
            <div className="brax-navbar">
                { this.state.scrollable ? (
                    <BraxIcon
                        icon="arrow"
                        rotation={90}
                        className={this.state.scrollPosition === 'start' ? 'disabled' : undefined}
                        onClick={() => this.scroll(-1)}
                    />
                ) : null }

                <nav ref={ref => this.setRef(ref)} onScroll={() => this.checkSize()}>
                    { this.props.itens.map(item => {
                        const status = item.key === this.state.active
                                ? 'brax-active'
                            : item.disabled
                                ? 'brax-disable'
                            : undefined;

                        return (
                            <a
                                key={item.key}
                                className={status}
                                onClick={event => status !== 'brax-disable' ? this.change(item.key, event) : void 0}
                            >
                                { item.content }
                            </a>
                        )
                    }) }
                </nav>

                { this.state.scrollable ? (
                    <BraxIcon
                        icon="arrow"
                        rotation={-90}
                        className={this.state.scrollPosition === 'end' ? 'disabled' : undefined}
                        onClick={() => this.scroll(+1)}
                    />
                ) : null }
            </div>
        );
    }

}
